import React, { useState } from 'react';
import { Button, Input, Popconfirm, message } from 'antd';
import { useMutation } from '@apollo/client';
import { MAX_LIST_SIZE } from '../constants';
import { BizAoiAttributeRelatedAddress } from '../../aoi-platform/graphql/queries/getAoiAttributeDetail';
import {
    ADD_AOI_RELATED_ADDRESSES,
    AddAoiRelatedAddressesResponse,
} from '../../aoi-platform/graphql/mutations/addAoiRelatedAddresses';
import {
    REMOVE_AOI_RELATED_ADDRESSES,
    RemoveAoiRelatedAddressesResponse,
} from '../graphql/mutations/removeAoiRelatedAddresses';

interface Props {
    aoiId: number;
    zipcode: string;
    addresses: BizAoiAttributeRelatedAddress[];
    readonly?: boolean;
    /** 增删成功后回调 (父级 refetch 详情)。 */
    onChanged: () => void;
}

/**
 * 关联地址编辑器: 已关联列表 (逐条可删, removeAoiRelatedAddresses) + 多行文本批量添加 (addAoiRelatedAddresses)。
 */
const RelatedAddressEditor: React.FC<Props> = ({ aoiId, zipcode, addresses, readonly = false, onChanged }) => {
    const [addText, setAddText] = useState('');
    const [adding, setAdding] = useState(false);

    const [addMutation, { loading: addLoading }] = useMutation<AddAoiRelatedAddressesResponse>(ADD_AOI_RELATED_ADDRESSES);
    const [removeMutation, { loading: removeLoading }] = useMutation<RemoveAoiRelatedAddressesResponse>(REMOVE_AOI_RELATED_ADDRESSES);

    const handleAdd = async () => {
        const lines = addText.split('\n').map((s) => s.trim()).filter(Boolean);
        if (lines.length === 0) {
            message.warning('请输入至少一条地址');
            return;
        }
        if (lines.length > MAX_LIST_SIZE) {
            message.warning(`单次最多添加 ${MAX_LIST_SIZE} 条地址`);
            return;
        }
        try {
            const { data } = await addMutation({ variables: { input: { aoiId, zipcode, addressStrings: lines } } });
            const result = data?.addAoiRelatedAddresses;
            if (!result) {
                message.error('添加失败, 请稍后重试');
                return;
            }
            if (result.failedCount > 0) {
                const firstFail = result.items.find((it) => !it.success);
                message.warning(
                    `成功 ${result.successCount} 条, 失败 ${result.failedCount} 条${firstFail?.errorMessage ? `: ${firstFail.errorMessage}` : ''}`,
                );
            } else {
                message.success(`已添加 ${result.successCount} 条地址`);
            }
            setAddText('');
            setAdding(false);
            onChanged();
        } catch (err) {
            message.error(`添加失败: ${err instanceof Error ? err.message : String(err)}`);
        }
    };

    const handleRemove = async (addressCoreId: number) => {
        try {
            await removeMutation({ variables: { input: { aoiId, zipcode, addressCoreIds: [addressCoreId] } } });
            message.success('已移除该地址');
            onChanged();
        } catch (err) {
            message.error(`移除失败: ${err instanceof Error ? err.message : String(err)}`);
        }
    };

    return (
        <div>
            <label className="mb-2 block text-sm" style={{ color: 'rgba(0,0,0,.88)' }}>
                关联地址
                <span style={{ color: '#a5adb9', fontWeight: 500 }}>（共 {addresses.length} 条）</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                {addresses.map((a) => (
                    <div
                        key={a.addressCoreId}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '7px 5px 7px 10px',
                            border: '1px solid #e5e9f0',
                            borderRadius: 8,
                            background: '#f7f9fc',
                        }}
                    >
                        <span style={{ flex: 1, fontSize: 12, color: '#334155', lineHeight: 1.4 }}>
                            {a.fullStandardAddress ?? `#${a.addressCoreId}`}
                        </span>
                        {!readonly && (
                            <Popconfirm
                                title="确认移除该关联地址?"
                                okText="移除"
                                cancelText="取消"
                                okButtonProps={{ danger: true, loading: removeLoading }}
                                onConfirm={() => handleRemove(a.addressCoreId)}
                            >
                                <button
                                    type="button"
                                    title="移除"
                                    style={{
                                        flex: 'none',
                                        width: 24,
                                        height: 24,
                                        border: 'none',
                                        borderRadius: 6,
                                        background: 'transparent',
                                        color: '#a5adb9',
                                        fontSize: 13,
                                        cursor: 'pointer',
                                    }}
                                >✕</button>
                            </Popconfirm>
                        )}
                    </div>
                ))}
                {addresses.length === 0 && (
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', padding: '4px 2px' }}>暂无关联地址</div>
                )}
            </div>
            {!readonly && (
                <div style={{ marginTop: 6 }}>
                    {adding ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <Input.TextArea
                                value={addText}
                                onChange={(e) => setAddText(e.target.value)}
                                placeholder={'每行一条地址, 如:\n100 Universal City Plaza, Universal City, CA 91608'}
                                autoSize={{ minRows: 3, maxRows: 6 }}
                            />
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => { setAdding(false); setAddText(''); }}>取消</Button>
                                <Button size="small" type="primary" loading={addLoading} onClick={handleAdd}>添加</Button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setAdding(true)}
                            style={{
                                width: '100%',
                                height: 34,
                                border: '1px dashed #cdd6e2',
                                borderRadius: 8,
                                background: '#fff',
                                color: 'rgba(0,0,0,.65)',
                                fontWeight: 600,
                                fontSize: 12,
                                cursor: 'pointer',
                            }}
                        >＋ 添加地址</button>
                    )}
                </div>
            )}
        </div>
    );
};

export default RelatedAddressEditor;
